import React, { useEffect, useRef } from 'react';
import s from './ChooseToolPanel.module.css';
import { BlockName } from '../../../../shared/types/projectTypes';
import { ReactElement } from 'react';
import IconTone from '../../../../shared/ui/icons/IconTone';
import IconImproveText from '../../../../shared/ui/icons/IconImproveText';
import IconReferences from '../../../../shared/ui/icons/IconReferences';
import IconAdjustVisuals from '../../../../shared/ui/icons/IconAdjustVisuals';

import IconWizardHat from '../../../../shared/ui/icons/IconWizardHat';

import { useDispatch, useSelector } from 'react-redux';
import {
    selectActiveEditorMode,
    selectActivePrompt,
} from '../../../../store/selectors/pageSelectors';
import {
    selectImageLayout,
    selectTemporaryTexts,
} from '../../../../store/selectors/imageSelectors';
import { setActiveSettingsSlider, toggleActivePrompt } from '../../../../store/slices/pageSlice';
import { useAppDispatch } from '../../../../store/store';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import IconStars from '../../../../shared/ui/icons/IconStars';
import IconTextOption from '../../../../shared/ui/icons/IconTextOption';
import IconAddText from '../../../../shared/ui/icons/IconAddText';
import { addText, setSelectedText } from '../../../../store/slices/imageSlice';
import { InlineText } from '../../../../shared/types/imagesType';
import { quillRef } from '../../../../store/ref/quillRef';
import { createRoot, Root } from 'react-dom/client';
import EmojiButton from '../../../text-editor-page/widgets/tools-buttons/EmojiButton';

type PromptPanelType = 'Visual' | 'Captions';

const BLOCKS: Array<{
    name: BlockName;
    label: string;
    icon: ReactElement;
    target: PromptPanelType;
}> = [
    {
        name: 'effects',
        label: 'Effects',
        icon: <IconStars width={22} height={22} />,
        target: 'Visual',
    },
    {
        name: 'add_text',
        label: 'Add text',
        icon: <IconAddText width={22} height={22} />,
        target: 'Visual',
    },
    {
        name: 'adjust_colors',
        label: 'Adjust colors',
        icon: <IconAdjustVisuals width={22} height={22} />,
        target: 'Visual',
    },
    {
        name: 'image_wizard',
        label: 'Image wizard',
        icon: <IconWizardHat width={22} height={22} />,
        target: 'Visual',
    },
    // {
    //     name: 'video',
    //     label: 'Video',
    //     icon: <IconWizardHat />,
    //     target: 'Visual',
    // },
    {
        name: 'tone_and_voice',
        label: 'Tone and voice',
        icon: <IconTone width={22} height={22} />,
        target: 'Captions',
    },
    {
        name: 'refine_text',
        label: 'Refine text',
        icon: <IconImproveText width={22} height={22} />,

        target: 'Captions',
    },
    {
        name: 'references',
        label: 'Search references',
        icon: <IconReferences width={22} height={22} />,

        target: 'Captions',
    },
    {
        name: 'text_options',
        label: 'Text options',
        icon: <IconTextOption width={22} height={22} />,
        target: 'Captions',
    },
];
const ChooseToolPanel = () => {
    const activeEditorMode = useSelector(selectActiveEditorMode);
    const activePrompt = useSelector(selectActivePrompt);
    const dispatch = useAppDispatch();
    const temperoryTexts = useSelector(selectTemporaryTexts);
    const texts = useSelector(selectImageLayout)?.inline_text;

    const toolsRef = useRef<(HTMLDivElement | null)[]>([]);
    const indicatorRef = useRef<HTMLDivElement | null>(null);

    const handleToggleBlock = async (block: BlockName) => {
        dispatch(toggleActivePrompt(block));
        dispatch(setActiveSettingsSlider(null));

        if (block === 'add_text') {
            if (temperoryTexts.length === 0) {
                handleAddText();
            } else if (texts?.length) {
                dispatch(setSelectedText(texts[0]));
            }
            dispatch(setActiveSettingsSlider('fonts'));
        }
    };
    const handleAddText = () => {
        const newText: InlineText = {
            id: `${Date.now()}`,
            content: 'Add text',
            location: { x: 200, y: 200 },
            color: { red: 255, green: 255, blue: 255, transparency: 1, gradient: 'none' },
            size: 40,
            font: 'Arial',
            rotation: 1,
            stroke: 0,
            stroke_color: { r: 255, g: 255, b: 255, a: 1 },
            text_shadow_offset_x: 0,
            text_shadow_offset_y: 0,
            text_shadow_blur: 0,
            text_shadow_color: { r: 0, g: 0, b: 0, a: 1 },
            background_radius: 0,
            background_width: 0,
            background_height: 0,
            background_color: { r: 255, g: 255, b: 255, a: 0 },
            style: 'none',
        };
        dispatch(addText(newText));
        dispatch(setSelectedText(newText));
    };

    const emojiRootRef = useRef<Root | null>(null);
    const emojiContainerRef = useRef<HTMLSpanElement | null>(null);

    useEffect(() => {
        const filteredBlocks = BLOCKS.filter((block) => block.target === activeEditorMode);
        const activeIndex = filteredBlocks.findIndex((block) => block.name === activePrompt);
        const activeTool = toolsRef.current[activeIndex];
        const indicator = indicatorRef.current;

        if (activeTool && indicator && activePrompt) {
            const toolRect = activeTool.getBoundingClientRect();
            const containerRect = activeTool.parentElement?.getBoundingClientRect();

            if (containerRect) {
                const offsetLeft = toolRect.left - containerRect.left;
                const width = toolRect.width;
                const height = toolRect.height;

                indicator.style.transform = `translateX(${offsetLeft}px)`;
                indicator.style.width = `${width}px`;
                indicator.style.height = `${height}px`;
                indicator.style.opacity = '1';
            }
        } else if (indicator) {
            indicator.style.opacity = '0';
        }
    }, [activePrompt, activeEditorMode]);

    useEffect(() => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        const toolbarEl = quill.getModule('toolbar')?.container as HTMLElement | undefined;
        if (!toolbarEl) return;

        const addEmoji = () => {
            if (emojiContainerRef.current) return;
            const span = document.createElement('span');
            span.className = 'ql-formats';
            toolbarEl.appendChild(span);
            emojiContainerRef.current = span;
            emojiRootRef.current = createRoot(span);
            emojiRootRef.current.render(<EmojiButton quillRef={quillRef} />);
        };

        const removeEmoji = () => {
            if (emojiRootRef.current) {
                emojiRootRef.current.unmount();
                emojiRootRef.current = null;
            }
            if (emojiContainerRef.current) {
                emojiContainerRef.current.remove();
                emojiContainerRef.current = null;
            }
        };
        toolbarEl.style.visibility = 'visible';
        addEmoji();

        // if (activePrompt === 'text_options') {
        //     toolbarEl.style.visibility = 'visible';
        //     addEmoji();
        // } else {

        //     toolbarEl.style.visibility = 'visible';
        // }

        // return () => {
        //     removeEmoji();
        //     toolbarEl.style.visibility = 'visible';
        // };
    }, [activePrompt]);

    return (
        <div className={s.container}>
            <div className={s.toolsWrapper}>
                <div className={s.activeIndicator} ref={indicatorRef}></div>
                {BLOCKS.filter((block) => block.target === activeEditorMode).map((block, index) => {
                    const isActive = block.name === activePrompt;

                    const iconWithColor = React.cloneElement(block.icon, {
                        ...(isActive ? { color: 'black' } : {}),
                    });
                    return (
                        <div
                            key={block.name}
                            ref={(el) => (toolsRef.current[index] = el)}
                            className={s.tool_item}
                            onClick={() => handleToggleBlock(block.name)}
                            data-tooltip-id={String(block.name)}
                        >
                            {iconWithColor}
                            <ReactTooltip
                                id={String(block.name)}
                                place="bottom"
                                content={block.label}
                                className={s.tooltip}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ChooseToolPanel;
